import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { verifyEmail, setupPassword } from '../../store/slices/authSlice';
import toast from 'react-hot-toast';

const EmailVerification = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading: authLoading } = useSelector((state) => state.auth);

  const [isVerifying, setIsVerifying] = useState(true);
  const [verificationData, setVerificationData] = useState(null);

  const [formData, setFormData] = useState({
    password: '',
    password_confirmation: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const email = searchParams.get('email');

  useEffect(() => {
    if (token && email) {
      handleEmailVerification();
    } else {
      toast.error('Invalid verification link');
      navigate('/');
    }
  }, [token, email]);

  const handleEmailVerification = async () => {
    try {
      setIsVerifying(true);
      const result = await dispatch(verifyEmail({ token, email })).unwrap();

      if (result) {
        setVerificationData(result);
        setIsVerifying(false);
      }
    } catch (error) {
      toast.error(error.message || 'Email verification failed');
      navigate('/');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const generatePassword = () => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setFormData({
      password: password,
      password_confirmation: password
    });
    toast.success('Strong password generated!');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.password_confirmation) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    try {
      const result = await dispatch(setupPassword({
        token: verificationData.token || token,
        email: verificationData.email || email,
        password: formData.password,
        password_confirmation: formData.password_confirmation
      })).unwrap();

      if (result.success) {
        toast.success('Profile completed! You can now login.');
        navigate('/?login_pop_up=1');
      }
    } catch (error) {
      toast.error(error || 'Failed to create password');
    }
  };

  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="relative w-16 h-16 mx-auto mb-4">
            <div className="absolute inset-0 border-4 border-[#03538f20] rounded-full"></div>
            <div className="absolute inset-0 border-4 border-t-[#03538f] rounded-full animate-spin"></div>
          </div>
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">Verifying your email...</h2>
          <p className="text-gray-500">Please wait while we verify your email address.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white pt-[100px] pb-[50px]">
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.08)] p-6 md:p-10 w-full max-w-[500px] border border-gray-100">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Complete Profile</h1>
            <p className="text-gray-500 text-sm">
              Please create your strong password or generate<br />
              one using the Password Generator.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              {/* Password Field */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="fas fa-key text-gray-400"></i>
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Password"
                  required
                  className="password-field block w-full pl-10 pr-24 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#ffd200] focus:border-transparent transition-all outline-none text-gray-700"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-1">
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                  <button
                    type="button"
                    onClick={generatePassword}
                    className="h-full px-3 bg-[#ffd200] text-black rounded-r-lg hover:bg-[#ffeb00] transition-colors flex items-center justify-center"
                    title="Generate Password"
                  >
                    <i className="fas fa-lock"></i>
                  </button>
                </div>
              </div>

              {/* Advance Setting Link Placeholder */}
              <div className="text-right">
                <button type="button" className="text-xs text-gray-500 hover:text-gray-700 underline">
                  Advance Setting
                </button>
              </div>

              {/* Confirm Password Field */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="fas fa-key text-gray-400"></i>
                </div>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="password_confirmation"
                  value={formData.password_confirmation}
                  onChange={handleInputChange}
                  placeholder="Confirm Password"
                  required
                  className="password-field block w-full pl-10 pr-10 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#ffd200] focus:border-transparent transition-all outline-none text-gray-700"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={authLoading}
              className="w-full py-4 bg-[#ffd200] text-black font-bold rounded-lg shadow-lg hover:shadow-xl hover:bg-[#ffeb00] active:scale-[0.98] transition-all disabled:opacity-50 uppercase tracking-wider"
            >
              {authLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="fas fa-spinner fa-spin"></i>
                  Processing...
                </span>
              ) : (
                'Submit'
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Footer Area Placeholder */}
      <div className="bg-gray-50/50 border-t border-gray-100 p-8 mt-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <h4 className="font-bold text-gray-800 mb-4 uppercase text-xs tracking-widest">For Customer</h4>
            <ul className="text-sm text-gray-500 space-y-2">
              <li>Ways to Fly</li>
              <li>FAQ</li>
              <li>Safety & Security</li>
              <li>News</li>
            </ul>
          </div>
          <div>
            <h4 className="font-bold text-gray-800 mb-4 uppercase text-xs tracking-widest">Company</h4>
            <ul className="text-sm text-gray-500 space-y-2">
              <li>About Us</li>
              <li>Contact Us</li>
              <li>Investor Relations</li>
              <li>Career</li>
            </ul>
          </div>
          <div>
            <h4 className="font-bold text-gray-800 mb-4 uppercase text-xs tracking-widest">Legal</h4>
            <ul className="text-sm text-gray-500 space-y-2">
              <li>Terms & Conditions</li>
              <li>Privacy Policy</li>
              <li>Refund & Cancellation</li>
            </ul>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default EmailVerification;